import React from "react";
import { BlockMath, InlineMath } from "react-katex";
import IntervalInput, {
  parseIntervalInput,
  intervalToLatex,
} from "../../../../components/inputs/IntervalInput";

const TOL = 1e-9;

const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

const VERTICAL_FACTORS = [2, 3, 0.5, 1 / 3, -2, -3, -0.5, -1 / 3];
const HORIZONTAL_FACTORS = [2, 3, 0.5, 1 / 3, -2, -3, -0.5, -1 / 3];

function interval(left, right, leftClosed = false, rightClosed = false) {
  return { left, right, leftClosed, rightClosed };
}

function eqNumber(a, b) {
  return (
    (a === Infinity && b === Infinity) ||
    (a === -Infinity && b === -Infinity) ||
    (Number.isFinite(a) && Number.isFinite(b) && Math.abs(a - b) <= TOL)
  );
}

function sameInterval(a, b) {
  return (
    eqNumber(a.left, b.left) &&
    eqNumber(a.right, b.right) &&
    !!a.leftClosed === !!b.leftClosed &&
    !!a.rightClosed === !!b.rightClosed
  );
}

function sameIntervals(parsed, expected) {
  if (!parsed || parsed.kind !== "intervals") return false;
  const got = parsed.intervals || [];
  if (got.length !== expected.length) return false;
  return expected.every((item, index) => sameInterval(got[index], item));
}

function intervalRaw(item) {
  const left = item.leftClosed ? "[" : "(";
  const right = item.rightClosed ? "]" : ")";
  return `${left}${item.left}; ${item.right}${right}`;
}

function intervalsLatex(intervals) {
  return intervalToLatex(intervals.map(intervalRaw).join(" ? "));
}

function factorLatex(factor) {
  if (factor === 0.5) return "\\frac{1}{2}";
  if (factor === -0.5) return "-\\frac{1}{2}";
  if (factor === 1 / 3) return "\\frac{1}{3}";
  if (factor === -1 / 3) return "-\\frac{1}{3}";
  return String(factor);
}

function requiredDivisorForIntegerResult(kind, factor) {
  const abs = Math.abs(factor);
  if (kind === "range") {
    if (abs === 0.5) return 2;
    if (abs === 1 / 3) return 3;
    return 1;
  }
  if (abs === 2) return 2;
  if (abs === 3) return 3;
  return 1;
}

function makeSourceInterval(kind, factor) {
  const divisor = requiredDivisorForIntegerResult(kind, factor);
  const multiplier = kind === "range" ? factor : 1 / factor;
  const maxResultAbs = 18;
  const maxSourceAbs = Math.max(divisor * 3, Math.min(12, Math.floor(maxResultAbs / Math.abs(multiplier))));
  const candidates = [];
  for (let value = -maxSourceAbs; value <= maxSourceAbs; value += 1) {
    if (value !== 0 && value % divisor === 0) candidates.push(value);
  }

  let left = 0;
  let right = 0;
  while (right - left < divisor * 2) {
    left = pick(candidates);
    right = pick(candidates);
    if (left > right) [left, right] = [right, left];
  }

  return interval(left, right, Math.random() < 0.55, Math.random() < 0.55);
}

function scaleInterval(source, multiplier) {
  const left = source.left * multiplier;
  const right = source.right * multiplier;

  if (multiplier >= 0) {
    return interval(left, right, source.leftClosed, source.rightClosed);
  }

  return interval(right, left, source.rightClosed, source.leftClosed);
}

export function generateTask(kind) {
  const transformKind = kind || pick(["rangeScale", "domainScale"]);
  const factor = pick(transformKind === "rangeScale" ? VERTICAL_FACTORS : HORIZONTAL_FACTORS);
  const source = makeSourceInterval(transformKind === "rangeScale" ? "range" : "domain", factor);
  const multiplier = transformKind === "rangeScale" ? factor : 1 / factor;
  const result = scaleInterval(source, multiplier);

  return {
    type: "ScaleSetTransformTask",
    data: {
      transformKind,
      factor,
      source: [source],
      result: [result],
    },
  };
}

export function renderTask({ data }) {
  if (data.transformKind === "rangeScale") {
    return (
      <div style={{ lineHeight: 1.6 }}>
        Zbiorem warto?ci funkcji <InlineMath math={"y=f(x)"} /> jest{" "}
        <InlineMath math={`ZW_f=${intervalsLatex(data.source)}`} />. Wyznacz zbi?r warto?ci funkcji{" "}
        <InlineMath math={`g(x)=${factorLatex(data.factor)}f(x)`} />.
      </div>
    );
  }

  return (
    <div style={{ lineHeight: 1.6 }}>
      Dziedzin? funkcji <InlineMath math={"y=f(x)"} /> jest{" "}
      <InlineMath math={`D_f=${intervalsLatex(data.source)}`} />. Wyznacz dziedzin? funkcji{" "}
      <InlineMath math={`g(x)=f(${factorLatex(data.factor)}x)`} />.
    </div>
  );
}

export function renderInput(task, userAnswer, onChange, disabled) {
  const label = task?.data?.transformKind === "rangeScale" ? "ZW_g" : "D_g";
  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ marginBottom: 4 }}>
        <InlineMath math={`${label}=`} />
      </div>
      <IntervalInput
        value={userAnswer}
        onChange={onChange}
        disabled={disabled}
        asString
        showPreview
        placeholder="np. [-6; 4) albo (-8; 2]"
      />
    </div>
  );
}

export function checkAnswer({ data }, userAnswer) {
  const parsed = typeof userAnswer === "string" ? parseIntervalInput(userAnswer) : userAnswer?.parsed;
  return sameIntervals(parsed, data.result);
}

export function renderAnswer({ data }) {
  const label = data.transformKind === "rangeScale" ? "ZW_g" : "D_g";
  return <InlineMath math={`${label}=${intervalsLatex(data.result)}`} />;
}

export function renderUserAnswer(arg1, arg2) {
  const task = arg1?.type === "ScaleSetTransformTask" ? arg1 : arg2;
  const userAnswer = arg1?.type === "ScaleSetTransformTask" ? arg2 : arg1;
  if (!userAnswer || (typeof userAnswer === "object" && !userAnswer.raw)) return <i>(brak odpowiedzi)</i>;
  const raw = typeof userAnswer === "string" ? userAnswer : userAnswer.raw;
  const label = task?.data?.transformKind === "rangeScale" ? "ZW_g" : "D_g";
  return <InlineMath math={`${label}=${intervalToLatex(raw)}`} />;
}

export function renderExplanation({ data }) {
  if (data.transformKind === "rangeScale") {
    return (
      <div style={{ lineHeight: 1.7 }}>
        <p>
          Dla <InlineMath math={`g(x)=${factorLatex(data.factor)}f(x)`} /> mno?ymy wszystkie warto?ci funkcji przez{" "}
          <InlineMath math={factorLatex(data.factor)} />.
        </p>
        <BlockMath math={`ZW_f=${intervalsLatex(data.source)}`} />
        <BlockMath math={`ZW_g=${intervalsLatex(data.result)}`} />
      </div>
    );
  }

  return (
    <div style={{ lineHeight: 1.7 }}>
      <p>
        Dla <InlineMath math={`g(x)=f(${factorLatex(data.factor)}x)`} /> do funkcji <InlineMath math={"f"} /> trafia{" "}
        <InlineMath math={`${factorLatex(data.factor)}x`} />. Dlatego dziedzin? dzielimy przez ten wsp??czynnik.
      </p>
      <BlockMath math={`D_f=${intervalsLatex(data.source)}`} />
      <BlockMath math={`D_g=${intervalsLatex(data.result)}`} />
    </div>
  );
}
